// src/components/RecalculatingState.js
//
// Shown when an API fetch completes but returns zero rows because the hourly
// backend task is actively rebuilding the betting database.
//
// Usage:  <RecalculatingState />
//         <RecalculatingState title="Refreshing Promo Data" />
//
// IMPORTANT: Do NOT show this during a loading/skeleton state. Only render
// when: loading === false  AND  rawData.length === 0.

import React, { useState, useEffect } from 'react';
import './RecalculatingState.css';

const SUBTITLES = [
  'Fetching & analyzing fresh betting data\u2026',
  'Calculating thousands of bets in real time.',
  'Scanning sportsbooks for the latest opportunities.',
  'Refreshing odds, props, and market data.',
  'The system is rebuilding fresh EV calculations.',
  'Fresh markets loading \u2014 thank you for your patience.',
];

export default function RecalculatingState({ title, className }) {
  const [subtitleIdx, setSubtitleIdx] = useState(0);
  const [fading,      setFading]      = useState(false);

  // Kick off the 4-second cycling timer.
  useEffect(() => {
    const id = setInterval(() => setFading(true), 4000);
    return () => clearInterval(id);
  }, []);

  // When fade-out begins, wait for the CSS transition then swap text.
  useEffect(() => {
    if (!fading) return;
    const t = setTimeout(() => {
      setSubtitleIdx(i => (i + 1) % SUBTITLES.length);
      setFading(false);
    }, 380);
    return () => clearTimeout(t);
  }, [fading]);

  return (
    <div
      className={`rs-root${className ? ` ${className}` : ''}`}
      role="status"
      aria-live="polite"
      aria-label="System is refreshing market data"
    >
      {/* ── Animated visual ──────────────────────────────────────────── */}
      <div className="rs-visual" aria-hidden="true">
        {/* Floating mini stat cards */}
        <div className="rs-float-card rs-float-card--1">
          <span className="rs-fc-label">EV+</span>
          <span className="rs-fc-value">+4.2%</span>
        </div>
        <div className="rs-float-card rs-float-card--2">
          <span className="rs-fc-label">Odds</span>
          <span className="rs-fc-value">&minus;115</span>
        </div>
        <div className="rs-float-card rs-float-card--3">
          <span className="rs-fc-label">Plays</span>
          <span className="rs-fc-value">&infin;</span>
        </div>

        {/* Central spinning glow ring */}
        <div className="rs-ring-wrap">
          <div className="rs-ring-outer" />
          <div className="rs-ring-inner">
            <div className="rs-ring-dot" />
          </div>
        </div>

        {/* Equalizer bars */}
        <div className="rs-bars">
          <div className="rs-bar rs-bar--1" />
          <div className="rs-bar rs-bar--2" />
          <div className="rs-bar rs-bar--3" />
          <div className="rs-bar rs-bar--4" />
          <div className="rs-bar rs-bar--5" />
        </div>
      </div>

      {/* ── Text ─────────────────────────────────────────────────────── */}
      <h3 className="rs-title">{title ?? 'Refreshing Market Data'}</h3>
      <p className={`rs-subtitle${fading ? ' rs-subtitle--fade' : ''}`}>
        {SUBTITLES[subtitleIdx]}
      </p>
      <p className="rs-hint">Data typically refreshes in under a minute.</p>
    </div>
  );
}
